import { PAGE_BG_STYLE } from "@/lib/pageBg";
import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useWalletSafe } from "@/hooks/useWalletSafe";
import { useWalletSupabaseSession } from "@/hooks/useWalletSupabaseSession";
import { useAirdropChallenge } from "@/hooks/useAirdropChallenge";
import { useIpfsUpload } from "@/hooks/useIpfsUpload";
import { useDeviceFingerprint } from "@/hooks/useDeviceFingerprint";
import { useStrikeStatus } from "@/hooks/useStrikeStatus";
import type { Session } from "@supabase/supabase-js";

import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import {
  Trophy, Gift, Clock, Users, Copy, Check, Rocket,
  Crown, Medal, Award, Share2, Sparkles, Upload,
  Image, Loader2, Zap, Target, ArrowDown, ExternalLink,
  ShieldCheck, AlertTriangle, Timer
} from "lucide-react";

/* ── Prize config ── */
const PRIZES = [
  { place: "1st", amount: "$50", icon: <Crown className="w-7 h-7 text-yellow-400" />, bg: "from-yellow-500/20 to-amber-500/10 border-yellow-500/30" },
  { place: "2nd", amount: "$25", icon: <Medal className="w-6 h-6 text-gray-300" />, bg: "from-gray-400/15 to-gray-500/5 border-gray-400/30" },
  { place: "3rd", amount: "$12.50", icon: <Medal className="w-6 h-6 text-amber-600" />, bg: "from-amber-600/15 to-amber-700/5 border-amber-600/30" },
  { place: "4th", amount: "$7.50", icon: <Award className="w-5 h-5 text-muted-foreground" />, bg: "from-muted/30 to-muted/10 border-border" },
  { place: "5th", amount: "$3", icon: <Award className="w-5 h-5 text-muted-foreground" />, bg: "from-muted/30 to-muted/10 border-border" },
];

const AIRDROP_PENDING_MINT_STORAGE_KEY = "flow_airdrop_pending_mint";
const AIRDROP_SHARE_STARTED_AT_STORAGE_KEY = "flow_airdrop_share_started_at";

type PendingMintData = {
  name: string;
  description: string | null;
  imageUrl: string;
  referralCode: string;
  profileDisplayName: string | null;
  profileId: string;
};

function generateDailyReferralCode(userId: string, walletAddress: string): string {
  const userFrag = userId.replace(/-/g, "").slice(0, 4);
  const walletFrag = walletAddress.slice(0, 4);
  const today = new Date().toISOString().split("T")[0];
  const dateHash = today.replace(/-/g, "").slice(4);
  return `FLOW-${userFrag}-${walletFrag}-${dateHash}`;
}

/* ── Helper: time until UTC midnight ── */
function useReferralExpiry() {
  const [timeLeft, setTimeLeft] = useState(() => calcTimeLeft());

  function calcTimeLeft() {
    const now = new Date();
    const midnight = new Date(now);
    midnight.setUTCHours(24, 0, 0, 0);
    const diff = Math.max(0, midnight.getTime() - now.getTime());
    const h = Math.floor(diff / (1000 * 60 * 60));
    const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const s = Math.floor((diff % (1000 * 60)) / 1000);
    return { h, m, s, expired: diff === 0 };
  }

  useEffect(() => {
    const iv = setInterval(() => setTimeLeft(calcTimeLeft()), 1000);
    return () => clearInterval(iv);
  }, []);

  return timeLeft;
}

export default function AirdropChallenge() {
  const [searchParams] = useSearchParams();
  const { connected, publicKey } = useWalletSafe();
  const { status: sessionStatus } = useWalletSupabaseSession();
  const { getComplianceData } = useDeviceFingerprint();
  const { uploadToIpfs, uploading: uploadingToIpfs, progress: ipfsProgress } = useIpfsUpload();
  const { combinedStatus } = useStrikeStatus();
  const walletAddress = publicKey?.toBase58() ?? "";

  const [session, setSession] = useState<Session | null>(null);
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_ev, s) => setSession(s));
    return () => subscription.unsubscribe();
  }, []);

  const userId = session?.user?.id;

  const {
    leaderboard, userStats, loading, contestActive, contestEnded,
    contestEnd, timeRemaining, registerContestMint, refreshLeaderboard,
  } = useAirdropChallenge(userId, walletAddress);

  const [countdown, setCountdown] = useState(timeRemaining());
  const [copied, setCopied] = useState(false);

  // Mint form state
  const [mintFile, setMintFile] = useState<File | null>(null);
  const [mintName, setMintName] = useState("");
  const [mintDesc, setMintDesc] = useState("");
  const [minting, setMinting] = useState(false);
  const [justMintedCode, setJustMintedCode] = useState<string | null>(null);
  const [antiFraudAgreed, setAntiFraudAgreed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mintSectionRef = useRef<HTMLDivElement>(null);

  // Pre-share pending mint state
  const [pendingMintData, setPendingMintData] = useState<PendingMintData | null>(null);
  const [shareCompleted, setShareCompleted] = useState(false);
  const [publishingMint, setPublishingMint] = useState(false);
  const shareClickedAtRef = useRef<number | null>(null);

  const buildReferralLink = useCallback(
    (code?: string | null) =>
      code ? `${window.location.origin}/airdrop-challenge?ref=${code}` : null,
    []
  );

  const clearPendingMintState = useCallback(() => {
    setPendingMintData(null);
    setShareCompleted(false);
    shareClickedAtRef.current = null;
    localStorage.removeItem(AIRDROP_PENDING_MINT_STORAGE_KEY);
    localStorage.removeItem(AIRDROP_SHARE_STARTED_AT_STORAGE_KEY);
  }, []);

  const startShareFlow = useCallback(
    (link: string) => {
      const startedAt = Date.now();
      shareClickedAtRef.current = startedAt;
      localStorage.setItem(AIRDROP_SHARE_STARTED_AT_STORAGE_KEY, String(startedAt));
      handleShareX(link);
    },
    []
  );

  useEffect(() => {
    const rawPendingMintData = localStorage.getItem(AIRDROP_PENDING_MINT_STORAGE_KEY);
    const rawShareStartedAt = localStorage.getItem(AIRDROP_SHARE_STARTED_AT_STORAGE_KEY);

    if (!rawPendingMintData) return;

    try {
      const restoredPendingMintData = JSON.parse(rawPendingMintData) as PendingMintData;
      setPendingMintData(restoredPendingMintData);

      if (!rawShareStartedAt) return;

      const parsedStartedAt = Number(rawShareStartedAt);
      if (!Number.isFinite(parsedStartedAt) || parsedStartedAt <= 0) {
        localStorage.removeItem(AIRDROP_SHARE_STARTED_AT_STORAGE_KEY);
        return;
      }

      shareClickedAtRef.current = parsedStartedAt;
      if (Date.now() - parsedStartedAt >= 10000) {
        setShareCompleted(true);
        shareClickedAtRef.current = null;
        localStorage.removeItem(AIRDROP_SHARE_STARTED_AT_STORAGE_KEY);
      }
    } catch {
      localStorage.removeItem(AIRDROP_PENDING_MINT_STORAGE_KEY);
      localStorage.removeItem(AIRDROP_SHARE_STARTED_AT_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    if (!pendingMintData) {
      localStorage.removeItem(AIRDROP_PENDING_MINT_STORAGE_KEY);
      return;
    }

    localStorage.setItem(
      AIRDROP_PENDING_MINT_STORAGE_KEY,
      JSON.stringify(pendingMintData)
    );
  }, [pendingMintData]);

  useEffect(() => {
    if (!pendingMintData || shareCompleted || !shareClickedAtRef.current) return;

    const elapsed = Date.now() - shareClickedAtRef.current;
    if (elapsed < 10000) return;

    setShareCompleted(true);
    shareClickedAtRef.current = null;
    localStorage.removeItem(AIRDROP_SHARE_STARTED_AT_STORAGE_KEY);
  }, [pendingMintData, shareCompleted]);

  // Visibility-based share confirmation: mark share as completed when user returns after 10s
  useEffect(() => {
    const handleVisibility = () => {
      if (
        document.visibilityState === "visible" &&
        shareClickedAtRef.current &&
        !shareCompleted &&
        pendingMintData
      ) {
        const elapsed = Date.now() - shareClickedAtRef.current;
        if (elapsed >= 10000) {
          setShareCompleted(true);
          shareClickedAtRef.current = null;
          localStorage.removeItem(AIRDROP_SHARE_STARTED_AT_STORAGE_KEY);
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [shareCompleted, pendingMintData]);

  const referralExpiry = useReferralExpiry();

  // Check banned status
  const isBanned = combinedStatus?.isAnyBanned ?? false;

  // Store referral code from URL
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) localStorage.setItem("airdrop_ref", ref);
  }, [searchParams]);

  // Countdown timer
  useEffect(() => {
    const interval = setInterval(() => setCountdown(timeRemaining()), 60_000);
    return () => clearInterval(interval);
  }, [timeRemaining]);

  const activeReferralCode = pendingMintData?.referralCode || userStats.referralCode || justMintedCode;
  const referralLink = buildReferralLink(activeReferralCode);

  const handleCopy = () => {
    if (!referralLink) return;
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast({ title: "Link copied!" });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareX = (link?: string) => {
    const shareLink = link || referralLink;
    if (!shareLink || !shareLink.includes("ref=")) {
      toast({ title: "Referral link not ready", description: "Prepare your NFT first to generate your referral code.", variant: "destructive" });
      return;
    }
    const tweetText = `🚀 I'm competing in the FLOW AirDrop Challenge!\n\nMint NFTs daily & climb the leaderboard to win EMON tokens.\n\n👇 Use MY referral link to join — it earns us BOTH points!`;
    const bounceParams = new URLSearchParams({
      tweet: tweetText,
      url: shareLink,
      name: "AirDrop Challenge",
      return_url: `${window.location.origin}/airdrop-challenge`,
    });
    window.location.assign(`/x-share-bounce?${bounceParams.toString()}`);
  };

  /* ── Phase 1: Prepare mint, then force share ── */
  const handleMint = async () => {
    if (!session?.user?.id || !mintFile) {
      toast({ title: "Please select an image", variant: "destructive" });
      return;
    }
    if (!contestActive) {
      toast({ title: "Contest is not active", variant: "destructive" });
      return;
    }
    if (!antiFraudAgreed) {
      toast({ title: "Please accept the anti-fraud agreement", variant: "destructive" });
      return;
    }
    if (isBanned) {
      toast({ title: "Account Suspended", description: "You cannot create NFTs while your account is suspended.", variant: "destructive" });
      return;
    }

    setMinting(true);
    try {
      // Get profile + check status
      const { data: profile } = await supabase
        .from("profiles")
        .select("id, display_name, is_banned, is_frozen, is_flagged, nfts_created_today, last_nft_created_date")
        .eq("id", session.user.id)
        .maybeSingle();

      if (!profile) throw new Error("Profile not found. Please visit the Dashboard first.");
      if (profile.is_banned || profile.is_frozen) throw new Error("Your account is restricted.");

      // Daily mint limit check (same as Dashboard)
      const today = new Date().toISOString().split("T")[0];
      const isToday = profile.last_nft_created_date === today;
      const dailyCount = isToday ? (profile.nfts_created_today ?? 0) : 0;
      if (dailyCount >= 10) throw new Error("Daily mint limit reached (max 10 per day).");

      const nameToUse = mintName.trim() || `${profile.display_name}'s NFT`;

      // Upload to IPFS
      const uploadResult = await uploadToIpfs(mintFile);
      if (!uploadResult.success || !uploadResult.url) throw new Error(uploadResult.error || "Upload failed");

      const referralCode = generateDailyReferralCode(profile.id, walletAddress);
      const nextPendingMintData = {
        name: nameToUse,
        description: mintDesc.trim() || null,
        imageUrl: uploadResult.url,
        referralCode,
        profileDisplayName: profile.display_name,
        profileId: profile.id,
      };

      setPendingMintData(nextPendingMintData);
      setShareCompleted(false);
      localStorage.setItem(
        AIRDROP_PENDING_MINT_STORAGE_KEY,
        JSON.stringify(nextPendingMintData)
      );
      localStorage.removeItem(AIRDROP_SHARE_STARTED_AT_STORAGE_KEY);

      setMintFile(null);
      setMintName("");
      setMintDesc("");
      setAntiFraudAgreed(false);
      if (fileInputRef.current) fileInputRef.current.value = "";

      toast({ title: "Image ready! Share on X to continue with mint." });
    } catch (error: any) {
      console.error("Mint error:", error);
      toast({ title: error?.message || "Failed to mint NFT", variant: "destructive" });
    }
    setMinting(false);
  };

  /* ── Phase 2: Publish mint only after successful share ── */
  const handleFinalizeMint = async () => {
    if (!pendingMintData || !session?.user?.id) {
      toast({ title: "Session expired. Please reconnect and try again.", variant: "destructive" });
      return;
    }
    if (!shareCompleted) {
      toast({ title: "Share required", description: "Complete the X share before publishing your NFT.", variant: "destructive" });
      return;
    }

    setPublishingMint(true);
    try {
      const { data: profile } = await supabase
        .from("profiles")
        .select("id, display_name, is_banned, is_frozen")
        .eq("id", session.user.id)
        .maybeSingle();

      if (!profile) throw new Error("Profile not found. Please visit the Dashboard first.");
      if (profile.is_banned || profile.is_frozen) throw new Error("Your account is restricted.");

      const initialPriceUsd = 1.0;
      const { data: newNFT, error: insertError } = await supabase
        .from("nfts")
        .insert({
          name: pendingMintData.name,
          description: pendingMintData.description,
          image_url: pendingMintData.imageUrl,
          creator_wallet: walletAddress,
          creator_name: pendingMintData.profileDisplayName,
          owner_id: pendingMintData.profileId,
          owner_wallet: walletAddress,
          owner_name: pendingMintData.profileDisplayName,
          price: initialPriceUsd,
          current_price: initialPriceUsd,
          base_price: initialPriceUsd,
          total_earned: 0,
          sales_count: 0,
          is_listed: true,
          image_verified: false,
          image_check_status: "pending" as const,
        })
        .select("id")
        .single();

      if (insertError) throw insertError;

      await supabase.from("nft_ownership_history").insert({
        nft_id: newNFT.id,
        from_wallet: null,
        to_wallet: walletAddress,
        price: 0,
        transaction_type: "mint",
      });

      const complianceData = getComplianceData();
      await supabase.from("transaction_logs").insert({
        user_id: pendingMintData.profileId,
        wallet_address: walletAddress,
        action_type: "nft_create",
        entity_type: "nft",
        entity_id: newNFT.id,
        details: {
          name: pendingMintData.name,
          image_url: pendingMintData.imageUrl,
          source: "airdrop_challenge",
          referral_code: pendingMintData.referralCode,
          _compliance: complianceData,
        },
      } as any);

      supabase
        .functions
        .invoke("verify-nft-image", { body: { nft_id: newNFT.id, image_url: pendingMintData.imageUrl } })
        .catch(console.error);

      const code = await registerContestMint(newNFT.id);
      setJustMintedCode(code || pendingMintData.referralCode);

      await refreshLeaderboard();

      clearPendingMintState();

      toast({ title: "NFT minted and published! 🎉" });
    } catch (error: any) {
      console.error("Finalize mint error:", error);
      toast({ title: error?.message || "Failed to mint NFT", variant: "destructive" });
    } finally {
      setPublishingMint(false);
    }
  };

  const isSessionSyncing = connected && (sessionStatus === "syncing" || sessionStatus === "none") && !session;
  const isLoggedIn = connected && sessionStatus === "valid" && !!session;

  return (
    <div className="min-h-screen relative text-foreground" style={PAGE_BG_STYLE}>
      <AppHeader />

      {/* ═══════ HERO + PRIZES ═══════ */}
      <section className="relative overflow-hidden pt-24 pb-12 px-4">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/8 via-accent/5 to-transparent pointer-events-none" />
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative max-w-5xl mx-auto text-center space-y-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 border border-primary/20 px-4 py-1.5 text-sm font-medium text-primary">
            <Rocket className="w-4 h-4" />
            {contestEnded ? "Contest Ended" : contestActive ? "LIVE NOW" : "Coming Soon"}
          </div>

          <h1 className="text-4xl md:text-6xl font-black tracking-tight leading-tight">
            AirDrop{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-purple-500 to-accent">
              Challenge
            </span>
          </h1>

          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Mint an NFT every day, share your referral link, and climb the leaderboard.
            The <strong className="text-foreground">Top 5</strong> win an EMON airdrop!
          </p>

          {/* Countdown */}
          {!contestEnded && (
            <div className="flex justify-center gap-3 sm:gap-4">
              {[
                { label: "Days", value: countdown.days },
                { label: "Hours", value: countdown.hours },
                { label: "Min", value: countdown.minutes },
              ].map((t) => (
                <div key={t.label} className="bg-card border border-border rounded-xl px-5 py-3 shadow-lg shadow-primary/5">
                  <div className="text-3xl md:text-4xl font-black text-foreground">{t.value}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">{t.label}</div>
                </div>
              ))}
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Ends: {contestEnd.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
          </p>

          {/* Prize Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 max-w-3xl mx-auto pt-4">
            {PRIZES.map((p) => (
              <div
                key={p.place}
                className={`flex flex-col items-center gap-2 rounded-xl border bg-gradient-to-b ${p.bg} p-4 text-center transition-transform hover:scale-105`}
              >
                {p.icon}
                <span className="text-xs font-bold text-muted-foreground uppercase">{p.place} Place</span>
                <span className="text-2xl font-black text-primary">{p.amount}</span>
                <span className="text-[10px] text-muted-foreground">in EMON</span>
              </div>
            ))}
          </div>

          {contestActive && (
            <Button
              size="lg"
              className="mt-4 gap-2"
              onClick={() => mintSectionRef.current?.scrollIntoView({ behavior: "smooth" })}
            >
              <ArrowDown className="w-4 h-4" /> Join Now — Mint Your NFT
            </Button>
          )}
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 pb-24 space-y-8">

        {/* ═══════ HOW IT WORKS ═══════ */}
        <section className="grid md:grid-cols-3 gap-4">
          {[
            {
              step: "1", title: "Mint an NFT",
              desc: "Create at least 1 NFT per day right here. Each mint earns 1 point and activates your referral link.",
              icon: <Sparkles className="w-6 h-6 text-primary" />,
            },
            {
              step: "2", title: "Share Your Referral",
              desc: "Copy your unique link and share it on X. Include it in your post! When someone mints their first NFT via your link, you earn 10 points!",
              icon: <Share2 className="w-6 h-6 text-accent" />,
            },
            {
              step: "3", title: "Climb the Leaderboard",
              desc: "Daily mint = 1 pt, converted referral = 10 pts. Top 5 at contest end win EMON!",
              icon: <Trophy className="w-6 h-6 text-yellow-500" />,
            },
          ].map((s) => (
            <Card key={s.step} className="relative overflow-hidden group hover:border-primary/30 transition-colors">
              <div className="absolute top-3 right-3 text-5xl font-black text-muted/20 group-hover:text-primary/10 transition-colors">{s.step}</div>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  {s.icon}
                  <CardTitle className="text-lg">{s.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{s.desc}</p>
              </CardContent>
            </Card>
          ))}
        </section>

        {/* ═══════ POINTS SYSTEM ═══════ */}
        <Card className="border-primary/20 overflow-hidden">
          <div className="h-0.5 bg-gradient-to-r from-primary/60 via-accent/40 to-primary/60" />
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Zap className="w-5 h-5 text-primary" /> Points System
            </CardTitle>
            <p className="text-xs text-muted-foreground">How you earn points in the AirDrop Challenge</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Daily Mint */}
              <div className="rounded-xl border border-border bg-muted/30 p-4 text-center space-y-2">
                <div className="mx-auto w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <div className="text-3xl font-black text-primary">+1</div>
                <div className="text-xs font-semibold text-foreground uppercase tracking-wide">pt / day</div>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  Mint at least 1 NFT per day. Each day you mint counts as 1 point.
                </p>
              </div>

              {/* Referral Conversion */}
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 text-center space-y-2 ring-1 ring-primary/10">
                <div className="mx-auto w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <div className="text-3xl font-black text-primary">+10</div>
                <div className="text-xs font-semibold text-foreground uppercase tracking-wide">pts / referral</div>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  When someone mints their <strong className="text-foreground">first NFT</strong> using your referral link, you earn 10 points.
                </p>
              </div>

              {/* Share on X */}
              <div className="rounded-xl border border-accent/30 bg-accent/5 p-4 text-center space-y-2">
                <div className="mx-auto w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                  <Share2 className="w-5 h-5 text-accent" />
                </div>
                <Badge variant="outline" className="text-accent border-accent/40 text-xs font-bold">REQUIRED</Badge>
                <div className="text-xs font-semibold text-foreground uppercase tracking-wide">Share on X</div>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  No direct points, but <strong className="text-foreground">mandatory</strong> to distribute your referral link and earn referral points.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ═══════ MINT NFT SECTION ═══════ */}
        <div ref={mintSectionRef} className="scroll-mt-24">
          <Card className="border-primary/30 overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-primary via-purple-500 to-accent" />
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Target className="w-5 h-5 text-primary" />
                </div>
                Daily Mint — Earn Your Points
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {userStats.hasMintedToday
                  ? "✅ You've already minted today! Come back tomorrow for more points."
                    : "Prepare your NFT, share your referral on X, then complete mint to log today's entry."}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {isSessionSyncing ? (
                <div className="text-center py-8 space-y-3">
                  <Loader2 className="w-10 h-10 text-primary mx-auto animate-spin" />
                  <p className="text-muted-foreground font-medium">Authenticating wallet…</p>
                  <p className="text-xs text-muted-foreground">Please wait while we verify your session.</p>
                </div>
              ) : !isLoggedIn ? (
                <div className="text-center py-8 space-y-3">
                  <Zap className="w-10 h-10 text-primary mx-auto" />
                  <p className="text-muted-foreground font-medium">Connect your wallet to participate</p>
                  <p className="text-xs text-muted-foreground">Use the button in the header to connect.</p>
                </div>
              ) : isBanned ? (
                <div className="text-center py-8 space-y-3">
                  <AlertTriangle className="w-10 h-10 text-destructive mx-auto" />
                  <p className="text-destructive font-medium">Your account is suspended</p>
                  <p className="text-xs text-muted-foreground">You cannot participate in the contest.</p>
                </div>
              ) : userStats.hasMintedToday ? (
                /* Already minted — show referral with expiry */
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-green-500">
                    <Check className="w-4 h-4" /> Today's mint registered!
                  </div>
                  {referralLink && (
                    <ReferralBlock
                      link={referralLink}
                      copied={copied}
                      onCopy={handleCopy}
                      onShareX={() => handleShareX()}
                      expiry={referralExpiry}
                    />
                  )}
                </div>
              ) : !contestActive ? (
                <p className="text-center text-muted-foreground py-6">
                  {contestEnded ? "The contest has ended." : "The contest hasn't started yet."}
                </p>
              ) : pendingMintData ? (
                <div className="space-y-4">
                  <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                      <Sparkles className="w-4 h-4 text-primary" />
                      NFT ready for share
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Share your referral link first, then come back here to complete mint and publish it on the site.
                    </p>
                  </div>

                  {referralLink && (
                    <ReferralBlock
                      link={referralLink}
                      copied={copied}
                      onCopy={handleCopy}
                      onShareX={() => startShareFlow(referralLink)}
                      expiry={referralExpiry}
                    />
                  )}

                  {shareCompleted ? (
                    <Button
                      className="w-full gap-2"
                      size="lg"
                      disabled={publishingMint}
                      onClick={handleFinalizeMint}
                    >
                      {publishingMint ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      {publishingMint ? "Minting…" : "Done — Mint & Publish"}
                    </Button>
                  ) : (
                    <div className="rounded-lg border border-accent/30 bg-accent/10 p-3 flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                      <p className="text-[11px] text-foreground leading-snug">
                        After sharing, return here after at least 10 seconds to unlock the final mint button.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                /* Mint form */
                <div className="space-y-4">
                  {/* File upload */}
                  <div
                    className="border-2 border-dashed border-border rounded-xl p-6 text-center cursor-pointer hover:border-primary/40 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {mintFile ? (
                      <div className="flex items-center justify-center gap-3">
                        <Image className="w-8 h-8 text-primary" />
                        <div className="text-left">
                          <p className="text-sm font-medium truncate max-w-[200px]">{mintFile.name}</p>
                          <p className="text-xs text-muted-foreground">{(mintFile.size / 1024 / 1024).toFixed(2)} MB</p>
                        </div>
                        <Button
                          variant="ghost" size="sm"
                          onClick={(e) => { e.stopPropagation(); setMintFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                        >
                          Change
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">Click to upload your NFT image</p>
                        <p className="text-xs text-muted-foreground mt-1">PNG, JPG, GIF, WEBP • Max 10 MB</p>
                      </>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file && file.size > 10 * 1024 * 1024) {
                          toast({ title: "File too large (max 10 MB)", variant: "destructive" });
                          return;
                        }
                        if (file) setMintFile(file);
                      }}
                    />
                  </div>

                  {/* Name + Description */}
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Input
                      placeholder="NFT name (optional)"
                      value={mintName}
                      onChange={(e) => setMintName(e.target.value)}
                      maxLength={60}
                    />
                    <Input
                      placeholder="Description (optional)"
                      value={mintDesc}
                      onChange={(e) => setMintDesc(e.target.value)}
                      maxLength={100}
                    />
                  </div>

                  {/* Anti-fraud checkbox */}
                  <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-3">
                    <Checkbox
                      id="antifraud-challenge"
                      checked={antiFraudAgreed}
                      onCheckedChange={(v) => setAntiFraudAgreed(v === true)}
                      className="mt-0.5"
                    />
                    <label htmlFor="antifraud-challenge" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
                      <ShieldCheck className="w-3.5 h-3.5 inline mr-1 text-primary" />
                      I confirm this is my original content and I agree to the anti-fraud policy. Multi-accounting and wash trading will result in disqualification.
                    </label>
                  </div>

                  {uploadingToIpfs && (
                    <div className="space-y-1">
                      <Progress value={ipfsProgress} className="h-2" />
                      <p className="text-xs text-muted-foreground text-center">Uploading to IPFS…</p>
                    </div>
                  )}

                  <Button
                    className="w-full gap-2"
                    size="lg"
                    disabled={!mintFile || minting || uploadingToIpfs || !antiFraudAgreed}
                    onClick={handleMint}
                  >
                    {minting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {minting ? "Preparing…" : "Prepare NFT & Continue to Share"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ═══════ YOUR STATS ═══════ */}
        {isLoggedIn && (
          <Card className="border-primary/20">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" /> Your Stats
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Referred by indicator */}
              {userStats.referredBy && (
                <div className="flex items-center gap-2 rounded-lg border border-green-500/20 bg-green-500/5 px-3 py-2 text-xs text-green-600">
                  <Check className="w-3.5 h-3.5 shrink-0" />
                  <span>You were referred by <span className="font-mono font-medium">{userStats.referredBy.slice(0, 4)}…{userStats.referredBy.slice(-4)}</span></span>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-xl bg-muted/50 border border-border p-4 text-center">
                  <div className="text-2xl font-black text-foreground">{userStats.rank ? `#${userStats.rank}` : "—"}</div>
                  <div className="text-xs text-muted-foreground mt-1">Rank</div>
                </div>
                <div className="rounded-xl bg-primary/10 border border-primary/30 p-4 text-center">
                  <div className="text-2xl font-black text-primary">{userStats.totalScore}</div>
                  <div className="text-xs text-muted-foreground mt-1">Total Score</div>
                </div>
                <div className="rounded-xl bg-muted/50 border border-border p-4 text-center">
                  <div className="text-2xl font-black text-foreground">{userStats.totalMintDays}</div>
                  <div className="text-xs text-muted-foreground mt-1">Mint Days</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">{userStats.totalMintDays} × 1 = <span className="text-primary font-semibold">{userStats.totalMintDays} pts</span></div>
                </div>
                <div className="rounded-xl bg-muted/50 border border-border p-4 text-center">
                  <div className="text-2xl font-black text-foreground">{userStats.totalReferrals}</div>
                  <div className="text-xs text-muted-foreground mt-1">Referrals</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">{userStats.totalReferrals} × 10 = <span className="text-primary font-semibold">{userStats.totalReferrals * 10} pts</span></div>
                </div>
              </div>

              {/* Referral details list */}
              {userStats.referralDetails.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                    <Gift className="w-3.5 h-3.5 text-primary" />
                    Your Referred Users ({userStats.referralDetails.length})
                  </p>
                  <div className="rounded-lg border border-border bg-muted/20 divide-y divide-border">
                    {userStats.referralDetails.map((ref, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 text-xs">
                        <span className="font-mono text-foreground">
                          {ref.referred_wallet.slice(0, 4)}…{ref.referred_wallet.slice(-4)}
                        </span>
                        <span className="text-muted-foreground">
                          {new Date(ref.converted_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </span>
                        <Badge variant="default" className="text-[10px] px-1.5 py-0">+10 pts</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ═══════ LEADERBOARD ═══════ */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" /> Leaderboard — Top 20
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => refreshLeaderboard()} className="text-xs gap-1">
              <Loader2 className="w-3 h-3" /> Refresh
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-14 w-full rounded-lg" />
                ))}
              </div>
            ) : leaderboard.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No participants yet — be the first! 🚀
              </p>
            ) : (
              <div className="space-y-2">
                {leaderboard.map((entry, idx) => {
                  const isCurrentUser = userId === entry.user_id || (walletAddress && entry.wallet_address && walletAddress === entry.wallet_address);
                  const prize = PRIZES[idx];
                  return (
                    <div
                      key={entry.user_id}
                      className={`flex items-center gap-3 rounded-xl px-4 py-3 transition-all ${
                        isCurrentUser
                          ? "bg-primary/10 border border-primary/30 shadow-sm shadow-primary/10"
                          : "bg-muted/30 hover:bg-muted/50"
                      }`}
                    >
                      {/* Position */}
                      <div className="w-8 text-center font-bold text-lg shrink-0">
                        {idx === 0 ? <Crown className="w-6 h-6 text-yellow-400 mx-auto" />
                          : idx === 1 ? <Medal className="w-5 h-5 text-gray-300 mx-auto" />
                          : idx === 2 ? <Medal className="w-5 h-5 text-amber-600 mx-auto" />
                          : <span className="text-muted-foreground">{idx + 1}</span>}
                      </div>

                      {/* Avatar + Name */}
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {entry.avatar_url ? (
                          <img src={entry.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground shrink-0">
                            {(entry.display_name || entry.wallet_address)?.[0]?.toUpperCase()}
                          </div>
                        )}
                        <div className="truncate">
                          <span className="font-medium text-sm">
                            {entry.display_name || `${entry.wallet_address.slice(0, 4)}…${entry.wallet_address.slice(-4)}`}
                          </span>
                          {isCurrentUser && <Badge variant="secondary" className="ml-2 text-xs">You</Badge>}
                        </div>
                      </div>

                      {/* Stats breakdown */}
                      <div className="hidden sm:flex items-center gap-1 text-[11px] text-muted-foreground font-mono">
                        <span>{entry.total_mint_days}d×1</span>
                        <span>+</span>
                        <span>{entry.total_referrals}r×10</span>
                      </div>

                      {/* Score + Prize */}
                      <div className="text-right shrink-0">
                        <div className="text-lg font-black text-primary">{entry.total_score}</div>
                        <div className="text-xs text-muted-foreground">
                          {prize ? <span className="text-yellow-500 font-medium">{prize.amount}</span> : "pts"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ═══════ RULES ═══════ */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Contest Rules</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p>• The contest runs for 90 days from the start date.</p>
            <p>• To participate, mint at least 1 NFT per day (you can do it right on this page).</p>
            <p>• Daily mint = 1 point. Converted referral (invited user mints) = 10 points.</p>
            <p>• Your referral link is only active on days you've minted and expires at midnight UTC.</p>
            <p>• Each wallet can only be referred once.</p>
            <p>• <strong className="text-foreground">You must share your referral link on X</strong> after minting — this is how you earn referral points!</p>
            <p>• EMON prizes will be distributed to the Top 5 within 7 days after the contest ends.</p>
            <p>• Fraudulent behavior (multi-accounting, wash trading) results in immediate disqualification.</p>
          </CardContent>
        </Card>
      </div>

      <Footer />

      {/* ═══════ PRE-MINT SHARE MODAL (Mandatory) ═══════ */}
      <Dialog open={!!pendingMintData} onOpenChange={() => { /* prevent dismiss */ }}>
        <DialogContent
          className="sm:max-w-md [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-primary" />
              Share on X Before Mint
            </DialogTitle>
            <DialogDescription>
              Complete sharing first. When you return here after at least 10 seconds, you'll unlock the final mint/publish step.
            </DialogDescription>
          </DialogHeader>
          {pendingMintData && (
            <div className="flex flex-col items-center gap-4">
              {pendingMintData.imageUrl && (
                <img
                  src={pendingMintData.imageUrl}
                  alt={pendingMintData.name}
                  className="w-32 h-32 rounded-lg object-cover border border-border"
                />
              )}
              <p className="text-sm font-medium text-foreground">{pendingMintData.name}</p>

              {/* Referral link in modal */}
              {pendingMintData.referralCode && (
                <div className="w-full rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-2">
                  <p className="text-xs font-semibold text-foreground flex items-center gap-1">
                    <Gift className="w-3.5 h-3.5 text-primary" /> Your Referral Link
                  </p>
                  <input
                    readOnly
                    value={`${window.location.origin}/airdrop-challenge?ref=${pendingMintData.referralCode}`}
                    className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[11px] truncate font-mono"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    ⚠️ Make sure this link is included in your X post!
                  </p>
                </div>
              )}

              {!shareCompleted ? (
                <Button
                  variant="default"
                  className="w-full gap-2"
                  onClick={() => {
                    const refLink = buildReferralLink(pendingMintData.referralCode);
                    if (!refLink) return;
                    startShareFlow(refLink);
                  }}
                >
                  <ExternalLink className="w-4 h-4" />
                  Share on X (Required)
                </Button>
              ) : (
                <div className="flex flex-col gap-2 w-full">
                  <div className="flex items-center justify-center gap-2 text-green-500 text-sm font-medium py-2">
                    <Check className="w-4 h-4" />
                    Share completed! Finalize mint to publish.
                  </div>
                  <Button
                    variant="default"
                    className="w-full gap-2"
                    disabled={publishingMint}
                    onClick={handleFinalizeMint}
                  >
                    {publishingMint ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {publishingMint ? "Minting…" : "Done — Mint & Publish"}
                  </Button>
                </div>
              )}

              {!shareCompleted && (
                <div className="space-y-2">
                  <div className="rounded-lg border border-accent/30 bg-accent/10 p-2.5 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                    <p className="text-[11px] text-foreground leading-snug">
                      <strong>Important:</strong> After sharing, switch back to your wallet app (Phantom/Solflare) to return here. It will be confirmed automatically.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ── Referral Block Sub-Component ── */
function ReferralBlock({
  link, copied, onCopy, onShareX, expiry,
}: {
  link: string; copied: boolean; onCopy: () => void; onShareX: () => void;
  expiry: { h: number; m: number; s: number; expired: boolean };
}) {
  return (
    <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-3">
      <p className="text-sm font-semibold text-foreground flex items-center gap-2">
        <Gift className="w-4 h-4 text-primary" /> Your Referral Link
      </p>

      {/* Expiry countdown */}
      {expiry.expired ? (
        <div className="flex items-center gap-2 text-xs text-destructive font-medium">
          <AlertTriangle className="w-3.5 h-3.5" />
          Your referral link has expired. Mint a new NFT tomorrow to get a fresh one!
        </div>
      ) : (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Timer className="w-3.5 h-3.5 text-primary" />
          Valid for <span className="font-mono font-medium text-foreground">{expiry.h}h {expiry.m}m {expiry.s}s</span> (until midnight UTC)
        </div>
      )}

      <div className="flex items-center gap-2">
        <input
          readOnly
          value={link}
          className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-xs truncate font-mono"
        />
        <Button size="sm" variant="outline" onClick={onCopy} className="shrink-0">
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>
      <Button size="sm" variant="default" className="w-full gap-2" onClick={onShareX}>
        <Share2 className="w-4 h-4" /> Share on X
      </Button>
      <p className="text-[11px] text-muted-foreground text-center">
        ⚠️ <strong>Include your referral link in your X post!</strong> When someone mints their first NFT via your link, you earn <strong>10 points</strong>.
      </p>
    </div>
  );
}
